import dayjs, { Dayjs } from "dayjs";
import {
  ILicenseSettings,
  IPackageInfo,
  ILicenseSettingInfo,
} from "../redux/types";

/**
 * Maps license settings boolean array to ILicenseSettings object
 * Uses the order from LicenseFileSetting enum
 */
export const mapLicenseSettings = (
  settings?: boolean[] | null,
  settingsOrder?: ILicenseSettingInfo[]
): ILicenseSettings => {
  if (!settings || settings.length < 10) {
    return {
      allowBetaFeatures: false,
      allowEmbeddedServer: false,
      allowLicensePicker: false,
      isDevModeEnabled: false,
      isNonProduction: false,
      isOem: false,
      isTrial: false,
      isTimeLimit: false,
      isOfflineKey: false,
      isRestrictedTrial: false,
    };
  }

  // If settings order is provided, use it to map correctly
  if (settingsOrder && settingsOrder.length > 0) {
    const settingsMap: Partial<ILicenseSettings> = {};

    settingsOrder.forEach((settingInfo) => {
      const settingName = settingInfo.name;
      const settingValue = settings[settingInfo.index] ?? false;

      // Map enum name to property name (camelCase)
      switch (settingName) {
        case "AllowBetaFeatures":
          settingsMap.allowBetaFeatures = settingValue;
          break;
        case "AllowEmbeddedServer":
          settingsMap.allowEmbeddedServer = settingValue;
          break;
        case "AllowLicensePicker":
          settingsMap.allowLicensePicker = settingValue;
          break;
        case "IsDevModeEnabled":
          settingsMap.isDevModeEnabled = settingValue;
          break;
        case "IsNonProduction":
          settingsMap.isNonProduction = settingValue;
          break;
        case "IsOem":
          settingsMap.isOem = settingValue;
          break;
        case "IsTrial":
          settingsMap.isTrial = settingValue;
          break;
        case "IsTimeLimit":
          settingsMap.isTimeLimit = settingValue;
          break;
        case "IsOfflineKey":
          settingsMap.isOfflineKey = settingValue;
          break;
        case "IsRestrictedTrial":
          settingsMap.isRestrictedTrial = settingValue;
          break;
      }
    });

    return {
      allowBetaFeatures: settingsMap.allowBetaFeatures ?? false,
      allowEmbeddedServer: settingsMap.allowEmbeddedServer ?? false,
      allowLicensePicker: settingsMap.allowLicensePicker ?? false,
      isDevModeEnabled: settingsMap.isDevModeEnabled ?? false,
      isNonProduction: settingsMap.isNonProduction ?? false,
      isOem: settingsMap.isOem ?? false,
      isTrial: settingsMap.isTrial ?? false,
      isTimeLimit: settingsMap.isTimeLimit ?? false,
      isOfflineKey: settingsMap.isOfflineKey ?? false,
      isRestrictedTrial: settingsMap.isRestrictedTrial ?? false,
    };
  }

  // Fallback: use default order if settingsOrder is not provided
  return {
    allowBetaFeatures: settings[0] ?? false,
    allowEmbeddedServer: settings[1] ?? false,
    allowLicensePicker: settings[2] ?? false,
    isDevModeEnabled: settings[3] ?? false,
    isNonProduction: settings[4] ?? false,
    isOem: settings[5] ?? false,
    isTrial: settings[6] ?? false,
    isTimeLimit: settings[7] ?? false,
    isOfflineKey: settings[8] ?? false,
    isRestrictedTrial: settings[9] ?? false,
  };
};

/**
 * Converts ILicenseSettings object back to boolean array
 * Uses the order from LicenseFileSetting enum
 */
export const convertLicenseSettingsToArray = (
  settings: ILicenseSettings,
  settingsOrder?: ILicenseSettingInfo[]
): boolean[] => {
  const result: boolean[] = new Array(10).fill(false);

  if (settingsOrder && settingsOrder.length > 0) {
    settingsOrder.forEach((settingInfo) => {
      const settingName = settingInfo.name;
      let value = false;

      switch (settingName) {
        case "AllowBetaFeatures":
          value = settings.allowBetaFeatures;
          break;
        case "AllowEmbeddedServer":
          value = settings.allowEmbeddedServer;
          break;
        case "AllowLicensePicker":
          value = settings.allowLicensePicker;
          break;
        case "IsDevModeEnabled":
          value = settings.isDevModeEnabled;
          break;
        case "IsNonProduction":
          value = settings.isNonProduction;
          break;
        case "IsOem":
          value = settings.isOem;
          break;
        case "IsTrial":
          value = settings.isTrial;
          break;
        case "IsTimeLimit":
          value = settings.isTimeLimit;
          break;
        case "IsOfflineKey":
          value = settings.isOfflineKey;
          break;
        case "IsRestrictedTrial":
          value = settings.isRestrictedTrial;
          break;
      }

      result[settingInfo.index] = value;
    });
  } else {
    // Fallback: use default order
    result[0] = settings.allowBetaFeatures;
    result[1] = settings.allowEmbeddedServer;
    result[2] = settings.allowLicensePicker;
    result[3] = settings.isDevModeEnabled;
    result[4] = settings.isNonProduction;
    result[5] = settings.isOem;
    result[6] = settings.isTrial;
    result[7] = settings.isTimeLimit;
    result[8] = settings.isOfflineKey;
    result[9] = settings.isRestrictedTrial;
  }

  return result;
};

/**
 * Gets package name (product edition) from packageIndex using package list
 * Returns the product edition name from LicConstants (e.g., "Data Pipeline Builder - Base Edition")
 */
export const getPackageNameFromIndex = (
  packageIndex: number,
  packageList?: IPackageInfo[]
): string | undefined => {
  if (!packageList || packageList.length === 0) {
    return undefined;
  }

  const packageInfo = packageList.find((p) => p.index === packageIndex);
  // Use name field which contains the product edition name from LicConstants
  return packageInfo?.name;
};

/**
 * Extracts feature group names from license features
 */
export const extractFeatureGroupNames = (features: any): string[] => {
  if (!features) return [];

  if (Array.isArray(features)) {
    return features
      .filter((f: any) => typeof f === "string" || f?.enabled === true)
      .map((f: any) => f?.name || f)
      .filter(Boolean);
  }

  if (typeof features === "object") {
    return Object.keys(features).filter((key) => features[key] === true);
  }

  return [];
};

/**
 * Parses expiry date from license
 */
export const parseExpiryDate = (
  expirationDate: Date | string | undefined
): Dayjs | undefined => {
  if (!expirationDate) return undefined;

  const date =
    typeof expirationDate === "string"
      ? new Date(expirationDate)
      : expirationDate;

  // Check for invalid dates
  if (
    date.getTime() === 0 ||
    date.getTime() === new Date("0001-01-01T00:00:00").getTime() ||
    isNaN(date.getTime())
  ) {
    return undefined;
  }

  return dayjs(date);
};
